/**
 * B+ tree
 * https://en.wikipedia.org/wiki/B%2B_tree
 */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
export var NodeType;
(function(NodeType) {
    NodeType[NodeType["Branch"] = 0] = "Branch";
    NodeType[NodeType["Leaf"] = 1] = "Leaf";
})(NodeType || (NodeType = {}));
function binarySearch(values, value, compare, findLast = false) {
    let low = 0;
    let high = values.length;
    while(low < high){
        // eslint-disable-next-line no-bitwise
        const mid = low + high >>> 1;
        const current = values[mid];
        const result = compare(current, value);
        if (findLast ? result <= 0 : result < 0) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }
    return high;
}
function last(array) {
    return array[array.length - 1];
}
export function isBranchNode(node) {
    return node.type === 0;
}
export function isLeafNode(node) {
    return node.type === 1;
}
function maxValue(node) {
    switch(node.type){
        case 0:
            return maxValue(last(node.values));
        case 1:
            return last(node.values);
        default:
            throw new Error('Unrecognized node type');
    }
}
function binarySearchNode(node, tree, data, inclusive = false) {
    if (isBranchNode(node)) {
        return binarySearch(node.values, data, tree.compareNode, inclusive);
    } else if (isLeafNode(node)) {
        return binarySearch(node.values, data, tree.compare, inclusive);
    }
    throw new Error('Unrecognized node type');
}
function summarize(node, tree, values = node.values) {
    if (!tree.summarize) {
        return undefined;
    }
    const toSummary = isBranchNode(node) ? ({ summary })=>summary : tree.summarize.map;
    return tree.summarize.reduce(values.map(toSummary));
}
function updateSummary(node, tree) {
    if (tree.summarize) {
        node.summary = summarize(node, tree);
    }
}
function updateSummaryRecursive(data, node, tree) {
    if (!tree.summarize) {
        return;
    }
    if (isBranchNode(node)) {
        const index = binarySearchNode(node, tree, data);
        updateSummaryRecursive(data, node.values[index], tree);
    }
    updateSummary(node, tree);
}
function splitNode(node, childNode, tree, index) {
    if (childNode.values.length > tree.maxNodeLength) {
        // Create a new node, splitting at the min node length
        const splitNode = {
            ...childNode,
            values: childNode.values.slice(tree.minNodeLength)
        };
        node.values.splice(index + 1, 0, splitNode);
        // Remove all values after the split point from the original node
        childNode.values.length = tree.minNodeLength;
        updateSummary(childNode, tree);
        updateSummary(splitNode, tree);
    }
}
function insertNode(data, node, tree) {
    const index = binarySearchNode(node, tree, data, true);
    if (isLeafNode(node)) {
        node.values.splice(index, 0, data);
        updateSummary(node, tree);
        return;
    } else if (!isBranchNode(node)) {
        throw new Error('Unrecognized node type');
    }
    const isLastIndex = index === node.values.length;
    const childNode = node.values[isLastIndex ? index - 1 : index];
    insertNode(data, childNode, tree);
    splitNode(node, childNode, tree, index);
    updateSummary(node, tree);
}
function removeNode(data, node, tree) {
    const index = binarySearchNode(node, tree, data);
    if (isLeafNode(node)) {
        if (tree.compare(data, node.values[index]) === 0) {
            node.values.splice(index, 1);
            updateSummary(node, tree);
        }
        return;
    } else if (!isBranchNode(node)) {
        throw new Error('Unrecognized node type');
    }
    const childNode = node.values[index];
    removeNode(data, childNode, tree);
    const currentLength = childNode.values.length;
    if (currentLength < tree.minNodeLength) {
        const nextNode = node.values[index + 1];
        const prevNode = node.values[index - 1];
        const nextLength = nextNode === null || nextNode === void 0 ? void 0 : nextNode.values.length;
        const prevLength = prevNode === null || prevNode === void 0 ? void 0 : prevNode.values.length;
        let borrowed = false;
        if (currentLength === tree.minNodeLength - 1) {
            // First, try to borrow from an adjacent node
            if (nextNode && nextLength > tree.minNodeLength) {
                childNode.values.push(nextNode.values.shift());
                updateSummary(childNode, tree);
                updateSummary(nextNode, tree);
                borrowed = true;
            } else if (prevNode && prevLength > tree.minNodeLength) {
                childNode.values.unshift(prevNode.values.pop());
                updateSummary(prevNode, tree);
                updateSummary(childNode, tree);
                borrowed = true;
            }
        }
        if (!borrowed) {
            // Otherwise, merge nodes if necessary
            if (nextNode && nextLength + currentLength <= tree.maxNodeLength) {
                childNode.values.push(...nextNode.values);
                node.values.splice(index + 1, 1);
                updateSummary(childNode, tree);
            } else if (prevNode && prevLength + currentLength <= tree.maxNodeLength) {
                prevNode.values.push(...childNode.values);
                node.values.splice(index, 1);
                updateSummary(prevNode, tree);
            }
        }
    }
    updateSummary(node, tree);
}
export class Tree {
    add(data) {
        insertNode(data, this.root, this);
        // When the root is at the max node length, increase the height of the tree.
        // The generic insert method will then handle splitting the node.
        if (this.root.values.length > this.maxNodeLength) {
            this.root = {
                type: 0,
                values: [
                    this.root
                ],
                summary: undefined
            };
            splitNode(this.root, this.root.values[0], this, 0);
            updateSummary(this.root, this);
        }
    }
    remove(data) {
        removeNode(data, this.root, this);
        // If there's only one node remaining in a root branch node, we can decrease
        // the height of the tree.
        if (this.root.values.length === 1 && isBranchNode(this.root)) {
            this.root = this.root.values[0];
            updateSummary(this.root, this);
        }
    }
    has(data) {
        let node = this.root;
        while(node){
            const index = binarySearchNode(node, this, data);
            if (isBranchNode(node)) {
                node = node.values[index];
            } else {
                return this.compare(data, node.values[index]) === 0;
            }
        }
        return false;
    }
    update(data) {
        updateSummaryRecursive(data, this.root, this);
    }
    summary(data, inclusive = false) {
        if (!this.summarize) {
            return undefined;
        }
        let node = this.root;
        const summaries = [];
        while(node){
            const index = binarySearchNode(node, this, data, inclusive);
            summaries.push(index === node.values.length ? node.summary : summarize(node, this, node.values.slice(0, index)));
            if (isBranchNode(node)) {
                node = node.values[index];
            } else {
                break;
            }
        }
        return this.summarize.reduce(summaries);
    }
    constructor({ branchingFactor, compare, summarize }){
        _define_property(this, "minNodeLength", void 0);
        _define_property(this, "maxNodeLength", void 0);
        _define_property(this, "compare", void 0);
        _define_property(this, "compareNode", void 0);
        _define_property(this, "root", void 0);
        _define_property(this, "summarize", void 0);
        this.compare = compare;
        this.compareNode = (a, b)=>compare(maxValue(a), b);
        this.summarize = summarize;
        this.maxNodeLength = branchingFactor;
        this.minNodeLength = Math.ceil(branchingFactor / 2);
        this.root = {
            type: 1,
            values: [],
            summary: undefined
        };
        updateSummary(this.root, this);
    }
}
function nodeToArray(node) {
    if (isBranchNode(node)) {
        return node.values.map(nodeToArray);
    } else if (isLeafNode(node)) {
        return node.values;
    }
    throw new Error('Invalid node');
}
export function treeToArray(tree) {
    return nodeToArray(tree.root);
}

//# sourceMappingURL=Tree.js.map